import { useEffect, useState } from "react";
import { getOfferByID, updateOfferByID, deleteOffer } from "../../../api/offers";
import { useParams, useNavigate } from "react-router-dom";
import { formatTime } from "../../../services/offers";
import { MdArrowBack } from "react-icons/md";
import TwoHandsClock from "../ScheduleClock";
import StateListModal from "../../StateListModal";
import OfferSection from "./OfferDetailsSection";
import CoverageSelector from "./Admin/CoverageSelector";
import SchedulePicker from "./Admin/ScheduleInput";
import OfferDeleteModal from "./Admin/OfferDeleteModal";
import InquireModal from "./InquireModal";
import toast from "react-hot-toast";
import { Offer, OfferUpdate } from "../../../models/offers";
import { getEmail } from "../../../api/users";
import { sendSupportEmail } from "../../../api/mail";
import { checkApplication, createApplication, deleteApplication } from "../../../api/offerApplications";
import TrafficAllowedSection from "./Section/TrafficAllowedSection";
import NegativeKeywordSection from "./Section/NegativeKeywordSection";
import CallApplicationModal from "../Forms/CallApplicationForm";
import { deleteZipList, downloadZipList, editZipList } from "../../../api/fileSystem";

type OfferDetailsProps = {
    mode: "owner" | "admin" | "user";
};

export default function OfferDetailsAll({ mode }: OfferDetailsProps) {
    const [offer, setOffer] = useState<Offer | null>(null);
    const [editedOffer, setEditedOffer] = useState<OfferUpdate | null>(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [showStates, setShowStates] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showInquire, setShowInquire] = useState(false);
    const [isApplied, setIsApplied] = useState(false);
    const [showCallForm, setShowCallForm] = useState(false);
    const [zipListFile, setZipListFile] = useState<File | null>(null);


    const { id } = useParams();
    const navigate = useNavigate();

    const isEditable = mode === "owner";
    const DEFAULT_RESTRICTIONS = `Creative Approval:
· All creatives, landing pages, and ad copy must be approved
· Traffic must come from owned and operated sites or approved media campaigns
· Unapproved materials, including those from sub-publishers, may lead to nonpayment and suspension

Advertising and Copy:
· No branded bidding, logos, or trademarks (see negative keyword list)
· No guaranteed discounts or dollar amounts in savings claims
· No use of "government programs" or "state savings programs" or similar misleading ad copy

Traffic Sources:
· DIRECT - CONSUMER INITIATED INBOUND CALLS ONLY
· No call transfers, including inbound calls routing through call centers
· No traffic from: SMS, incentivized sources, co-registration, email blasts, display ads, direct mail, or misdials`


    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!id) return;
                const data: Offer = await getOfferByID(Number(id));
                setOffer(data);
                if (isEditable) {
                    setEditedOffer({
                        ...data,
                        id: data.id!,
                        restrictions: data.restrictions?.trim() || DEFAULT_RESTRICTIONS
                    })
                }
            } catch (error) {
                console.error("Error fetching offer:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, isEditable]);

    useEffect(() => {
        const checkCurrApplied = async () => {
            if (!id) return;
            try {
                const res = await checkApplication(Number(id));
                setIsApplied(res.applied);
            } catch (error) {
                console.error("Error checking application:", error);
            }
        };
        checkCurrApplied();
    }, [id]);

    const handleChange = <K extends keyof OfferUpdate>(field: K, value: OfferUpdate[K]) => {
        setEditedOffer((prev) =>
            prev ? { ...prev, [field]: value } : { id: Number(id), [field]: value } as OfferUpdate
        );
    };

    const handleSave = async () => {
        if (!editedOffer) return;

        const hasStates =
            editedOffer.coverage?.state_list &&
            editedOffer.coverage.state_list.length > 0;

        const hasZips =
            (editedOffer.coverage?.number_of_zip_codes ?? 0) > 0;

        const isUploadingZipFile = !!zipListFile;


        // if (hasStates && (hasZips || isUploadingZipFile)) {
        //     toast.error("State list and ziplist cannot both exist. Choose only one.");
        //     return;
        // }



        if (!editedOffer.vertical?.trim()) {
            toast.error("Vertical is required");
            return;
        }

        if (!editedOffer.coverage) {
            toast.error("Coverage is required");
            return;
        }
        if (!editedOffer.description) {
            toast.error("Description is required");
            return;
        }

        if (!editedOffer.category?.trim()) {
            toast.error("Category is required");
            return;
        }

        if (!editedOffer.campaign_type) {
            toast.error("Campaign Type is required");
            return;
        }

        if (
            editedOffer.payout_type === "fixed" &&
            (!editedOffer.payout || editedOffer.payout <= 0)
        ) {
            toast.error("Payout must be greater than 0");
            return;
        }

        if (!editedOffer.conversion_type?.trim()) {
            toast.error("Conversion Type is required");
            return;
        }
        if (!hasStates && !hasZips && !isUploadingZipFile) {
            toast.error("Coverage is required.");
            return;
        }
        if (!Array.isArray(editedOffer.schedule?.days) || editedOffer.schedule?.days.length === 0) {
            toast.error("Schedule must include at least one day");
            return;
        }
        if (!editedOffer.languages || editedOffer.languages.length === 0) {
            toast.error("At least one language is required");
            return;
        }

        if (editedOffer.caps_amount === undefined || editedOffer.caps_amount === null) {
            toast.error("Caps amount is required");
            return;
        }

        // if (!editedOffer.caps_type?.trim()) {
        //     toast.error("Caps type is required");
        //     return;
        // }

        if (!editedOffer.traffic_allowed || editedOffer.traffic_allowed.length === 0) {
            toast.error("At least one allowed traffic type is required");
            return;
        }

        if (!editedOffer.restrictions?.trim()) {
            toast.error("Restrictions are required");
            return;
        }

        if (editedOffer.recording_permitted === undefined || editedOffer.recording_permitted === null) {
            toast.error("Recording permission must be specified");
            return;
        }

        if (editedOffer.live === undefined || editedOffer.live === null) {
            toast.error("Live flag must be specified");
            return;
        }

        if (editedOffer.visible === undefined || editedOffer.visible === null) {
            toast.error("Visibility flag must be specified");
            return;
        }

        if (editedOffer.campaign_type === "Calls") {
            if (editedOffer.conversion_type === "Duration") {
                if (!editedOffer.call_type?.flex) {
                    toast.error("Call type is required for Duration calls");
                    return;
                }
                if (editedOffer.call_type.flex === "fixed" && !editedOffer.connected_duration) {
                    toast.error("Connected duration is required for fixed Duration calls");
                    return;
                }
                if (!editedOffer.call_type?.on_type) {
                    toast.error("Criteria is required for Duration calls");
                    return;
                }
            }
        }



        try {
            if (!editedOffer || !offer) return;

            const payload: OfferUpdate = { ...editedOffer };

            if (
                payload.conversion_type != "Duration"
            ) {
                payload.call_type = {
                    on_type: null,
                    flex: null
                };
            }

            if (payload.payout_type === "variable") delete payload.payout;
            delete payload.is_supply;
            const updated = await updateOfferByID(payload);
            setOffer(updated);

            const hadZipCodes = offer.coverage?.number_of_zip_codes ?? 0 > 0;
            const nowStateList = payload.coverage?.state_list?.length ?? 0 > 0;

            if (hadZipCodes && nowStateList) {
                try {
                    await deleteZipList(offer.id);
                    setZipListFile(null);
                } catch (err) {
                    console.error("Failed to delete old ziplist", err);
                }
            }

            if ((editedOffer.coverage?.number_of_zip_codes ?? 0) > 0 && zipListFile) {
                await editZipList(zipListFile, updated.id);
            }
            toast.success("Offer updated successfully");
            setEditMode(false);

        } catch (error) {
            console.error(error);
            toast.error("Failed to update offer");
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await deleteOffer(Number(id));
            await deleteZipList(Number(id));
            navigate(-1);
        } catch (error) {
            console.error(error);
        }
    };

    const handleApplyOrRemove = async () => {
        if (!offer?.id) return;

        try {
            if (isApplied) {
                await deleteApplication(offer.id);
                toast.success("Application removed!");
                setIsApplied(false);
            } else {
                if (offer.campaign_type === "Calls")
                    setShowCallForm(true);
                else {
                    await applyToOffer();
                    setIsApplied(true);
                    setShowCallForm(false);
                }
            }
        } catch (error) {
            console.error("Error applying/removing application:", error);
            toast.error("Something went wrong");
        }
    };


    const handleFormSubmit = async (formData: any) => {
        try {
            await applyToOffer(formData);
            setIsApplied(true);
            setShowCallForm(false);
        } catch (error) {
            console.error("Error submitting application:", error);
            toast.error("Something went wrong");
        }
    };

    const onDownloadZipsClicked = async (offer: Offer) => {
        try {
            await downloadZipList(offer.id);
            toast.success("Ziplist download started");
        } catch (err) {
            toast.error("Failed to download Ziplist");
        }
    }

    const applyToOffer = async (formData?: {
        offer_id: number
        user_email?: string
        date_applied?: string
        promotion_description?: string
        traffic_ownership?: string
        landing_page?: string
        call_volume?: string
        other_rates?: string
    }) => {
        if (!offer?.id || !offer?.category) {
            toast.error("Offer information missing");
            return;
        }

        try {
            const data = await getEmail();

            let body =
                `The user: ${data.email} has applied to offer "${offer.category}" (ID: ${offer.id}).
Campaign type: ${offer.campaign_type}`;

            if (formData?.promotion_description)
                body += `\nPromotion description: ${formData.promotion_description}`;

            if (formData?.traffic_ownership)
                body += `\nTraffic ownership: ${formData.traffic_ownership}`;

            if (formData?.landing_page)
                body += `\nLanding page: ${formData.landing_page}`;

            if (formData?.call_volume)
                body += `\nCall volume: ${formData.call_volume}`;

            if (formData?.other_rates)
                body += `\nOther rates: ${formData.other_rates}`;

            await sendSupportEmail({
                subject: "RPM Marketplace - Offer Application",
                body,
                sender_email: data.email,
                admin_email: offer.offer_creator
            });

            await createApplication({
                offer_id: offer.id,
                campaign_type: offer.campaign_type,
                promotion_description: formData?.promotion_description ?? null,
                traffic_ownership: formData?.traffic_ownership ?? null,
                landing_page: formData?.landing_page ?? null,
                call_volume: formData?.call_volume ?? null,
                other_rates: formData?.other_rates ?? null,
            })

            toast.success("Applied successfully!");
        } catch (error) {
            console.error("Error applying to offer:", error);
            toast.error("Failed to apply");
        }
    };

    if (loading) return <p className="text-center mt-10">Loading...</p>;
    if (!offer) return <p className="text-center mt-10">Offer not found.</p>;

    return (
        <div className="bg-[#F3F4F6] min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow px-8 py-8 flex flex-col gap-8">
                {/* Back Button */}
                <button
                    onClick={() => {
                        if (editMode) {
                            setEditMode(false);
                            setEditedOffer(offer ? { ...offer, id: offer.id! } : null);
                        } else {
                            navigate(-1);
                        }
                    }}
                    className="bg-[#FF7A00] w-10 h-10 flex items-center justify-center text-white rounded-full shadow-md"
                >
                    <MdArrowBack />
                </button>

                {/* Header */}
                <div className="border-b border-gray-200 pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    {editMode && isEditable ? (
                        <div className="flex flex-col gap-2">
                            <select
                                className="px-3 py-1 bg-orange-500/10 text-orange-500 rounded-full font-semibold"
                                value={editedOffer?.vertical ?? ""}
                                onChange={(e) => handleChange("vertical", e.target.value)}
                            >
                                <option value="" disabled>Select vertical</option>
                                <option value="Insurance">Insurance</option>
                                <option value="Home Services">Home Services</option>
                                <option value="Legal">Legal</option>
                                <option value="Debt">Debt</option>
                                <option value="Travel">Travel</option>
                                <option value="Other">Other</option>
                            </select>

                            <input
                                className="text-3xl font-bold border px-2 py-1 rounded"
                                value={editedOffer?.category ?? ""}
                                onChange={(e) => handleChange("category", e.target.value)}
                            />

                            <select
                                className="px-3 py-1 bg-orange-500/10 text-orange-500 rounded-full font-semibold"
                                value={editedOffer?.campaign_type ?? ""}
                                onChange={(e) => handleChange("campaign_type", e.target.value)}
                            >
                                <option value="" disabled>Select Campaign Type</option>
                                <option value="Calls">Calls</option>
                                <option value="Leads">Leads</option>
                                <option value="Traffic">Traffic</option>
                            </select>

                            {!editedOffer?.is_supply && (
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editedOffer?.live ?? false}
                                            onChange={(e) => handleChange("live", e.target.checked)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {editedOffer?.live ? "Live" : "Offline"}
                                        </span>
                                        <span
                                            className={`w-3 h-3 rounded-full ${editedOffer?.live ? "bg-green-500" : "bg-gray-400"}`}
                                        />
                                    </label>
                                    < label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editedOffer?.visible ?? false}
                                            onChange={(e) => handleChange("visible", e.target.checked)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {editedOffer?.visible ? "Visible" : "Hidden"}
                                        </span>
                                    </label>
                                </div>
                            )
                            }

                        </div>
                    ) : (
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                            <div className="flex gap-3 mt-2 items-center">
                                <span className="mt-1 px-3 py-1 bg-orange-500/10 text-orange-500 rounded-full text-sm font-semibold">
                                    {offer.vertical}
                                </span>
                                <span className="mt-1 px-3 py-1 bg-orange-500/10 text-orange-500 rounded-full text-sm font-semibold">
                                    {offer.campaign_type}
                                </span>
                            </div>
                            <h1 className="text-3xl font-bold break-words flex items-center gap-2">
                                {offer.category}
                                <span
                                    className={`w-4 h-4 rounded-full ${offer.live ? 'bg-green-500' : 'bg-gray-400'} relative top-[4px]`}
                                    title={offer.live ? "Live" : "Offline"}
                                />
                            </h1>
                        </div>
                    )}

                    {!isEditable && (
                        < p className="text-sm text-gray-500">
                            Last updated:{" "}
                            {new Date(offer.last_updated).toLocaleDateString("en-US", {
                                timeZone: "America/Chicago"
                            })}
                        </p>
                    )
                    }

                    <div className="flex gap-2">
                        {isEditable && (
                            <>
                                <button
                                    onClick={() => (editMode ? handleSave() : setEditMode(true))}
                                    className="bg-[#FF7A00] text-white px-4 py-2 rounded"
                                >
                                    {editMode ? "Save" : "Edit"}
                                </button>
                                {editMode && (
                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="bg-red-600 text-white px-4 py-2 rounded"
                                    >
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Sections */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Payout */}

                    <OfferSection label="Payout">
                        {editMode && isEditable ? (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <select
                                    className="border p-1 rounded text-sm"
                                    value={editedOffer?.payout_type ?? "fixed"}
                                    onChange={(e) => handleChange("payout_type", e.target.value)}
                                >
                                    <option value="fixed">Fixed</option>
                                    <option value="variable">Variable</option>
                                </select>
                                {editedOffer?.payout_type === "fixed" && (
                                    <input
                                        type="number"
                                        className="border p-1 rounded w-full sm:w-32"
                                        value={editedOffer?.payout ?? ""}
                                        onChange={(e) => handleChange(
                                            "payout",
                                            e.target.value === "" ? undefined : Number(e.target.value))}
                                    />
                                )}
                            </div>
                        ) : (
                            <p className="text-lg font-bold text-orange-500">
                                {offer.payout_type === "fixed" ? `$${offer.payout}` : "Variable"}
                            </p>
                        )}
                    </OfferSection>


                    <OfferSection label="Conversion">
                        {editMode && isEditable ? (
                            <div className="flex flex-col gap-3">
                                {/* Conversion Type */}
                                <select
                                    className="border p-1 rounded w-full"
                                    value={editedOffer?.conversion_type ?? ""}
                                    onChange={(e) => handleChange("conversion_type", e.target.value)}
                                >
                                    <option value="">Select Conversion Type</option>
                                    <option value="CPA">CPA</option>
                                    <option value="CPL">CPL</option>

                                    {editedOffer?.campaign_type === "Calls" && <option value="Duration">Duration</option>}
                                </select>

                                {/* Duration Settings — only show if Calls and Duration */}
                                {editedOffer?.campaign_type === "Calls" &&
                                    editedOffer?.conversion_type === "Duration" && (
                                        <>
                                            {/* Duration Type */}
                                            <select
                                                className="border p-1 rounded w-full"
                                                value={editedOffer?.call_type?.flex ?? ""}
                                                onChange={(e) => {
                                                    const newFlex = e.target.value
                                                    handleChange("call_type", {
                                                        ...editedOffer?.call_type,
                                                        flex: newFlex,
                                                    })
                                                    if (newFlex !== "fixed") handleChange("connected_duration", undefined)
                                                }}
                                            >
                                                <option value="">Select Duration Type</option>
                                                <option value="fixed">Fixed</option>
                                                <option value="variable">Variable</option>
                                            </select>

                                            {/* Fixed Duration */}
                                            {editedOffer?.call_type?.flex === "fixed" && (
                                                <input
                                                    type="number"
                                                    className="border p-1 rounded w-full"
                                                    placeholder="Enter duration in seconds"
                                                    value={editedOffer?.connected_duration ?? ""}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            "connected_duration",
                                                            e.target.value === "" ? undefined : Number(e.target.value)
                                                        )
                                                    }
                                                />
                                            )}

                                            {/* Criteria */}
                                            <select
                                                className="border p-1 rounded w-full"
                                                value={editedOffer?.call_type?.on_type ?? ""}
                                                onChange={(e) =>
                                                    handleChange("call_type", {
                                                        ...editedOffer?.call_type,
                                                        on_type: e.target.value,
                                                    })
                                                }
                                            >
                                                <option value="">Select Criteria</option>
                                                <option value="Connected Call Duration">Connected Call Duration</option>
                                                <option value="Total Call Duration">Total Call Duration</option>
                                            </select>
                                        </>
                                    )}
                            </div>
                        ) : (
                            // Display mode
                            <div className="flex flex-row items-center w-full">
                                {offer.conversion_type === "CPA" ? (
                                    <p className="text-lg font-bold text-orange-500">CPA</p>
                                ) : offer.conversion_type === "CPL" ? (
                                    <p className="text-lg font-bold text-orange-500">CPL</p>
                                ) : offer.call_type?.flex === "variable" ? (
                                    <p className="text-lg font-bold text-orange-500">Variable</p>
                                ) : offer.connected_duration ? (
                                    <p className="text-lg font-bold text-orange-500">
                                        {offer.connected_duration}s
                                    </p>
                                ) : (
                                    <p className="text-lg font-bold text-orange-500">N/A</p>
                                )}

                                {offer.call_type?.on_type && (
                                    <p className="text-xs text-gray-400 ml-auto">
                                        On {offer.call_type.on_type}
                                    </p>
                                )}
                            </div>
                        )}
                    </OfferSection>

                    <OfferSection label="Coverage">
                        {editMode && isEditable ? (
                            <CoverageSelector
                                editedOffer={editedOffer}
                                handleChange={handleChange}
                                setZipListFile={setZipListFile}
                            />
                        ) : (
                            <>
                                {offer?.coverage?.state_list?.length ? (
                                    <>
                                        {offer.coverage.state_list.length === 1 &&
                                            offer.coverage.state_list[0] === "Nationwide" ? (
                                            <span className="text-lg font-bold text-orange-500">Nationwide</span>
                                        ) : (
                                            <button
                                                className="text-lg font-bold text-orange-500 underline"
                                                onClick={() => setShowStates(true)}
                                            >
                                                View {offer.coverage.state_list.length} States
                                            </button>
                                        )}
                                    </>
                                ) : offer?.coverage?.number_of_zip_codes ?? 0 > 0 ? (
                                    <>
                                        <span className="text-lg font-bold text-orange-500">
                                            {offer.coverage.number_of_zip_codes} Zip Codes
                                        </span>
                                        <button
                                            onClick={() => onDownloadZipsClicked(offer)}
                                            className="ml-4 px-3 py-1 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                                        >
                                            Download Ziplist
                                        </button>
                                    </>
                                ) : (
                                    <span className="text-lg font-bold text-orange-500">No coverage data</span>
                                )}


                            </>
                        )}
                    </OfferSection>


                    {/* Schedule */}
                    <OfferSection label="Schedule">
                        {editMode && isEditable ? (
                            <SchedulePicker
                                schedule={offer.schedule}
                                onChange={(newSchedule) => handleChange("schedule", newSchedule)}
                            />
                        ) : (
                            <>
                                <div className="flex justify-center items-center gap-1 mt-2">
                                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                                        <div
                                            key={day}
                                            className={`w-12 h-12 flex items-center justify-center text-base font-medium rounded border border-black text-white
                            ${offer?.schedule?.days?.includes(day) ? "bg-[#FF7A00]" : "bg-gray-300"}`}
                                        >
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-center items-center mt-2 gap-2">
                                    <TwoHandsClock
                                        hour1={offer?.schedule?.hours?.[0] ?? 0}
                                        hour2={offer?.schedule?.hours?.[1] ?? 0}
                                        size={75}
                                    />

                                    {offer?.schedule?.hours?.[0] === offer?.schedule?.hours?.[1] &&
                                        offer?.schedule?.days?.length === 7 ? (
                                        <p className="text-base text-black font-bold">Operates 24/7</p>
                                    ) : offer?.schedule?.hours?.[0] === offer?.schedule?.hours?.[1] ? (
                                        <p className="text-base text-black font-bold">Operates 24 Hours</p>
                                    ) : (
                                        <p className="text-base text-black font-bold">
                                            {formatTime(offer?.schedule?.hours?.[0])} - {formatTime(offer?.schedule?.hours?.[1])} CST
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </OfferSection>
                    <OfferSection label="Languages">
                        {editMode && isEditable ? (
                            <div className="space-y-1">
                                {["English", "Spanish"].map((lang) => (
                                    <label key={lang} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={editedOffer?.languages?.includes(lang) || false}
                                            onChange={(e) => {
                                                const current = editedOffer?.languages ?? []
                                                const updated = e.target.checked
                                                    ? [...current, lang]
                                                    : current.filter((l) => l !== lang)
                                                handleChange("languages", updated)
                                            }}
                                        />
                                        <span>{lang}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            <p className="text-lg font-bold text-orange-500">
                                {offer.languages.join(", ")}
                            </p>
                        )}
                    </OfferSection>

                    <OfferSection label="Caps">
                        {editMode && isEditable ? (
                            <div className="flex flex-row gap-2 w-full">
                                <input
                                    type="number"
                                    className="border p-1 rounded w-full"
                                    placeholder="Amount"
                                    value={editedOffer?.caps_amount ?? ""}
                                    onChange={(e) => handleChange(
                                        "caps_amount",
                                        e.target.value === "" ? undefined : Number(e.target.value))
                                    }
                                />
                                <select
                                    className="border p-1 rounded w-full"
                                    value={editedOffer?.caps_type ?? ""}
                                    onChange={(e) => handleChange("caps_type", e.target.value)}
                                >
                                    <option value="">Select type</option>
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Globally">Globally</option>
                                    <option value="Testing Cap">Testing Cap</option>
                                </select>
                            </div>
                        ) : (
                            <div className="flex flex-row items-center w-full">
                                {offer.caps_amount ? (
                                    <>
                                        <p className="text-lg font-bold text-orange-500">{offer.caps_amount}</p>
                                        <p className="text-xs text-gray-400 ml-auto">{offer.caps_type}</p>
                                    </>
                                ) : (
                                    <p className="text-lg font-bold text-orange-500">None</p>
                                )}
                            </div>
                        )}
                    </OfferSection>

                    <OfferSection label={offer.is_supply ? "Traffic No" : "Traffic Allowed"} >
                        <TrafficAllowedSection
                            editMode={editMode}
                            isEditable={isEditable}
                            trafficAllowed={editMode ? editedOffer?.traffic_allowed ?? [] : offer.traffic_allowed ?? []}
                            handleChange={(updated) => handleChange("traffic_allowed", updated)}
                            options={[
                                "Paid Search",
                                "SEO",
                                "Social Media",
                                "Display Ads",
                                "Print",
                                "Radio/TV",
                                "Directory Assistance",
                                "Free 411",
                                "Carrier Intercept",
                                "Mis-Dials",
                                "1800 Numbers",
                                "Direct Mail",
                                "SMS",
                                "Email Marketing",
                                "Outbound Transfer",
                                "Facebook Marketplace",
                                "GMB",
                                "Other",
                            ]}
                        />
                    </OfferSection>
                    <OfferSection label="Description">
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.description ?? ""}
                                onChange={(e) => handleChange("description", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed whitespace-pre-line">{offer.description}</p>
                        )}
                    </OfferSection>
                    {!offer.is_supply && (
                        <OfferSection label="Restrictions" className="sm:col-span-2">
                            {editMode && isEditable ? (
                                <textarea
                                    className="border p-2 rounded w-full h-64 resize-y"
                                    value={
                                        editedOffer?.restrictions?.trim() || DEFAULT_RESTRICTIONS
                                    }
                                    onChange={(e) => handleChange("restrictions", e.target.value)}
                                />
                            ) : (
                                <p className="text-lg font-bold text-orange-500 leading-relaxed whitespace-pre-line">
                                    {offer.restrictions}
                                </p>
                            )}
                        </OfferSection>
                    )}
                    <OfferSection label="Creative Assets" show={editMode || !!offer.creative_assets}>
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.creative_assets ?? ""}
                                onChange={(e) => handleChange("creative_assets", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed  whitespace-pre-line">{offer.creative_assets}</p>
                        )}
                    </OfferSection>
                    <OfferSection label="Target Profile" show={editMode || !!offer.target_profile}>
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.target_profile ?? ""}
                                onChange={(e) => handleChange("target_profile", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed whitespace-pre-line">{offer.target_profile}</p>
                        )}
                    </OfferSection>
                    {!offer.is_supply && (

                        <OfferSection label="Negative Keywords" show={editMode || !!offer.negative_keyword_list?.length} >
                            <NegativeKeywordSection
                                keywords={editMode ? editedOffer?.negative_keyword_list ?? [] : offer.negative_keyword_list ?? []}
                                editMode={editMode}
                                isEditable={isEditable}
                                onChange={(newList) => handleChange("negative_keyword_list", newList)}
                            />
                        </OfferSection>
                    )}
                    <OfferSection label="IVR Type" show={editMode || !!offer.ivr_type}>
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.ivr_type ?? ""}
                                onChange={(e) => handleChange("ivr_type", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed whitespace-pre-line">{offer.ivr_type}</p>
                        )}
                    </OfferSection>
                    <OfferSection label="IVR Details" show={editMode || !!offer.ivr_details}>
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.ivr_details ?? ""}
                                onChange={(e) => handleChange("ivr_details", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed">{offer.ivr_details}</p>
                        )}
                    </OfferSection>
                    <OfferSection label="Approved Ad Copy" show={editMode || !!offer.approved_ad_copy}>
                        {editMode && isEditable ? (
                            <textarea
                                className="border p-2 rounded w-full h-64 resize-y"
                                value={editedOffer?.approved_ad_copy ?? ""}
                                onChange={(e) => handleChange("approved_ad_copy", e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-bold text-orange-500 leading-relaxed">{offer.approved_ad_copy}</p>
                        )}
                    </OfferSection>
                    {!offer.is_supply && (
                        (editMode && editedOffer?.conversion_type === "Duration") ||
                        (!editMode && offer.conversion_type === "Duration")) && (
                            <OfferSection label="Recording Permitted">
                                {editMode && isEditable ? (
                                    <select
                                        className="border p-2 rounded w-full"
                                        value={editedOffer?.recording_permitted ? "yes" : "no"}
                                        onChange={(e) =>
                                            handleChange("recording_permitted", e.target.value === "yes")
                                        }
                                    >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                ) : (
                                    <p className="text-lg font-bold text-orange-500">
                                        {offer.recording_permitted ? "Yes" : "No"}
                                    </p>
                                )}
                            </OfferSection>
                        )}
                </div>
                {mode === "user" && (
                    <div className="pt-6 flex flex-col sm:flex-row justify-center gap-6">
                        <button
                            className={`px-6 py-2 rounded text-white ${isApplied ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"
                                }`}
                            onClick={handleApplyOrRemove}
                        >
                            {isApplied ? "Remove Application" : "Apply for Offer"}
                        </button>

                        <button
                            className="bg-white text-orange-500 border border-orange-500 px-6 py-2 rounded flex flex-col items-center leading-tight"
                            onClick={() => setShowInquire(true)}
                        >
                            <span className="text-xs text-[#606060]">Have a question about this offer?</span>
                            <span className="font-semibold">Email Us</span>
                        </button>
                    </div>
                )}

            </div>
            {
                showStates && (
                    <StateListModal
                        states={offer.coverage?.state_list ?? []}
                        onClose={() => setShowStates(false)}
                    />
                )
            }
            {
                showDeleteModal && (
                    <OfferDeleteModal
                        onDelete={() => { handleDelete() }}
                        onClose={() => setShowStates(false)}
                    />
                )
            }
            {
                showInquire && (
                    <InquireModal
                        offer={offer}
                        isOpen={showInquire}
                        onClose={() => setShowInquire(false)}
                    />
                )
            }
            {
                showCallForm && (
                    <CallApplicationModal
                        isOpen={showCallForm}
                        onClose={() => setShowCallForm(false)}
                        onSubmit={handleFormSubmit}
                    />
                )
            }
        </div >
    );
}

